import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import {
  requireLinkedContext,
  isMultiShopRedirect,
  type LinkedContext,
  type ChronicleLink,
} from "../data/chronicleContext";

interface ChronicleContextValue extends LinkedContext {
  refreshContext: () => Promise<void>;
}

const ChronicleContext = createContext<ChronicleContextValue | undefined>(undefined);

interface ChronicleContextWrapperProps {
  children: ReactNode;
}

/**
 * Wrapper component that provides Chronicle context (shopId, customerId) to children.
 * Handles loading state and redirects for unlinked/multi-shop users.
 */
export const ChronicleContextWrapper = ({ children }: ChronicleContextWrapperProps) => {
  const navigate = useNavigate();
  const [context, setContext] = useState<LinkedContext | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadContext = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await requireLinkedContext();

      if (isMultiShopRedirect(result)) {
        // Multi-shop user without selection - redirect to shop selector
        navigate("/chronicle/select-shop", { 
          replace: true,
          state: { links: result.links }
        });
        return;
      }

      // Single shop or valid multi-shop selection
      setContext(result);
    } catch (err) {
      console.error("Failed to load Chronicle context:", err);
      setError("Unable to load your account. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadContext();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center space-y-4 px-4">
        <p className="text-destructive">{error}</p>
        <button
          onClick={loadContext}
          className="text-primary underline text-sm"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!context) {
    return null;
  }

  return (
    <ChronicleContext.Provider value={{ ...context, refreshContext: loadContext }}>
      {children}
    </ChronicleContext.Provider>
  );
};

/**
 * Hook to access Chronicle context (shopId, customerId).
 * Must be used within ChronicleContextWrapper.
 */
/**
 * CHRON-UI-1: the same context, or null outside ChronicleContextWrapper. For layout chrome (the notification badge in
 * ChronicleLayout) that renders above the per-route wrapper; the throwing hook crashed every signed-in portal page.
 */
export const useOptionalChronicleContext = (): ChronicleContextValue | null => useContext(ChronicleContext) ?? null;

export const useChronicleContext = (): ChronicleContextValue => {
  const context = useContext(ChronicleContext);
  if (context === undefined) {
    throw new Error("useChronicleContext must be used within ChronicleContextWrapper");
  }
  return context;
};

export default ChronicleContextWrapper;
